import { formatDate } from "%/date";
import { Button } from "@/Button";
import DateButton from "@/common/DateButton";
import MessageModal from "@/MessageModal";
import { Dialog } from "@/Dialog";
import ToggleSwitch from "@/common/ToggleSwitch";
import { ValidateSelectList } from "@/SelectList";
import { ValidateTextarea } from "@/Textarea";
import { Controller } from "react-hook-form";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { useRouter } from "@tanstack/react-router";
import useNotifySendSavedSchedule, {
  type GalleryImage,
} from "^/business/notify/useNotifySendSavedSchedule";
import { ValidateInput } from "@/Input";
import { BusinessNoticeTempDetailResponse } from "~/types/common";
import { ValidateSelectListMulti } from "@/SelectListMulti";

// NOTE(사진 첨부 미구현)
// 저장된 일정 공지도 앨범 조회(albumData)가 연결돼 있지 않다.
// 이전에는 외부 스톡 이미지(unsplash) 2장을 더미로 두고 있었으나,
// 다른 사업장의 사진이 학부모에게 발송될 수 있어 제거했다.
// 앨범 연동은 useNotifySendSavedNotify 의 패턴을 따르면 된다.
const galleryData: GalleryImage[] = [];

const SendSavedSchedule = ({
  data,
  editMode = false,
}: {
  data: BusinessNoticeTempDetailResponse["data"];
  editMode?: boolean;
}) => {
  const router = useRouter();
  const {
    // 폼 관련
    formMethods: {
      handleSubmit,
      control,
      register,
      // watch,
      formState: { isValid, errors },
    },

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    selectedDates,
    setSelectedDates,
    selectedImgDate,
    setSelectedImgDate,

    // 모달 관련
    calendarModal: { isOpenModal, clickModal, closeModal },
    imageModal: { isOpenModal: isOpenImageModal, closeModal: closeImageModal },
    imgDateModal: {
      isOpenModal: isOpenImgDateModal,
      closeModal: closeImgDateModal,
    },

    // 이미지 관련
    // selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    // removeSelectedImage,
    handleImgModal,

    // 토글 관련
    // isToggle,
    // setIsToggle,
    isTimeToggle,
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,

    // 제출 관련
    bindSubmit,
    isSubmitting,
  } = useNotifySendSavedSchedule(data, editMode);

  return (
    <>
      <form className="flex-col flex-auto gap-1 px-5 bg-[#F5F5F5] grid grid-cols-1 animate-fadeIn">
        <div className="mb-5 animate-slideInFromTop">
          <Controller
            name="recipient"
            control={control}
            rules={{ required: "필수 선택입니다." }}
            render={({ field }) => (
              <ValidateSelectListMulti
                onChange={(selectedValues) => {
                  field.onChange(selectedValues);
                }}
                label="수신"
                placeholder="반려견 이름 선택 (다중선택 가능)"
                options={
                  selectListData?.recipients?.map((recipient) => ({
                    value: recipient.petSeq.toString(),
                    label: recipient.petName,
                  })) || []
                }
                value={
                  Array.isArray(field.value)
                    ? field.value
                    : field.value
                      ? [field.value]
                      : []
                }
                errorMessage={errors?.recipient?.message}
              />
            )}
          />
          <div className="my-4">
            <ValidateInput
              className="w-full"
              label="제목"
              placeholder="제목을 입력하세요."
              errorMessage={errors?.notiTitle?.message}
              {...register("notiTitle", {
                required: { value: true, message: "제목을 입력하세요" },
              })}
            />
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="flex justify-between items-center mb-1">
              <p className="mb-2 text-sm font-medium">발송시간</p>
              <ToggleSwitch
                checked={isTimeToggle}
                onChange={handleTimeToggleChange}
              />
            </div>
            {isTimeToggle && (
              <div className="flex gap-1 items-center mb-1">
                <DateButton
                  value={
                    selectedDates.length > 0
                      ? `${formatDate(selectedDates[selectedDates.length - 1])}`
                      : date
                  }
                  onClick={() => clickModal()}
                />
                <Controller
                  name="hour"
                  control={control}
                  rules={{ required: "시간을 선택하세요" }}
                  render={({ field }) => {
                    const hourScalar =
                      typeof field.value === "string"
                        ? field.value
                        : (field.value as { value?: string })?.value ?? "";
                    return (
                      <ValidateSelectList
                        isDefaultValue={true}
                        placeholder="시"
                        isSearchable={false}
                        options={Array.from({ length: 24 }, (_, i) => ({
                          value: (i + 1).toString().padStart(2, "0"),
                          label: (i + 1).toString().padStart(2, "0"),
                        }))}
                        boxHeight={20}
                        className="h-10 text-lg text-center"
                        value={
                          hourScalar
                            ? { value: hourScalar, label: hourScalar }
                            : null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value ?? "")
                        }
                      />
                    );
                  }}
                />
                <span className="text-[12px]">시</span>
                <Controller
                  name="minute"
                  control={control}
                  rules={{ required: "분을 선택하세요" }}
                  render={({ field }) => {
                    const minuteScalar =
                      typeof field.value === "string"
                        ? field.value
                        : (field.value as { value?: string })?.value ?? "";
                    return (
                      <ValidateSelectList
                        isDefaultValue={true}
                        placeholder="분"
                        isSearchable={false}
                        options={Array.from({ length: 6 }, (_, i) => ({
                          value: (i * 10).toString().padStart(2, "0"),
                          label: (i * 10).toString().padStart(2, "0"),
                        }))}
                        boxHeight={20}
                        className="h-10 text-lg text-center"
                        value={
                          minuteScalar
                            ? { value: minuteScalar, label: minuteScalar }
                            : null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value ?? "")
                        }
                      />
                    );
                  }}
                />
                <span className="text-[12px]">분</span>
              </div>
            )}
          </div>
          <div className="my-5">
            <Controller
              name="content"
              control={control}
              rules={{ required: "필수 선택입니다." }}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full h-100"
                  label="내용"
                  rows={10}
                />
              )}
            />
          </div>
        </div>
      </form>
      <div className="my-5">
        <CalendarComponent
          disabled={false}
          disablePast={true}
          multiSelect={true}
          selectedDays={selectedDates}
          onChange={(days) => {
            if (Array.isArray(days)) {
              setSelectedDates(days);
            }
          }}
          schedules={{}}
        />
        {/* 선택된 날짜들 표시 */}
        {selectedDates.length > 0 && (
          <div className="p-3 mt-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gray-700">선택된 날짜:</p>
              <button
                type="button"
                onClick={() => setSelectedDates([])}
                className="text-xs text-red-500 hover:text-red-700"
              >
                전체 삭제
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedDates.map((date, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs text-white rounded-full bg-yellow"
                >
                  {formatDate(date)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="w-full px-5 py-6 mb-20 bg-[#F5F5F5] flex gap-2">
        {editMode ? (
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit((data) => bindSubmit(data, false))}
            disabled={!isValid || isSubmitting}
          >
            <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px]">
              {isSubmitting ? "수정 중..." : "수정 완료"}
            </div>
          </Button>
        ) : (
          <>
            <Button
              className="w-full bg-[#F5F5F5]"
              style={{ pointerEvents: "auto" }}
              onClick={handleSubmit((data) => bindSubmit(data, true))}
              disabled={!isValid || isSubmitting}
            >
              <div className="py-3 mx-auto w-full font-semibold text-center text-yellow-500 bg-white rounded-full border border-yellow text-[16px] text-yellow">
                저장하기
              </div>
            </Button>
            <Button
              className="w-full bg-[#F5F5F5]"
              style={{ pointerEvents: "auto" }}
              onClick={handleSubmit((data) => bindSubmit(data, false))}
              disabled={!isValid || isSubmitting}
            >
              <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px]">
                즉시 발송
              </div>
            </Button>
          </>
        )}
      </div>
      {/* 모달 영역 */}
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          multiSelect={true}
          selectedDays={selectedDates}
          onChange={(days) => {
            if (Array.isArray(days)) {
              setSelectedDates(days);
            }
          }}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={() => {
              closeModal();
              // 선택된 날짜가 있으면 DateButton 업데이트
              if (selectedDates.length > 0) {
                console.log(
                  "선택된 날짜들:",
                  selectedDates.map((date) => formatDate(date))
                );
              }
            }}
          >
            확인
          </button>
        </div>
      </MessageModal>
      <MessageModal
        isOpen={isOpenImgDateModal}
        onRequestClose={closeImgDateModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          dayOnClick={(day) => {
            setSelectedImgDate(day);
          }}
          selectedDay={selectedImgDate}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImgDateModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={closeImgDateModal}
          >
            확인
          </button>
        </div>
      </MessageModal>
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenImageModal}
        onRequestClose={closeImageModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col gap-2 overflow-y-auto h-[500px]">
          {galleryData.length === 0 ? (
            <div className="flex flex-col flex-1 gap-1 justify-center items-center px-6 text-center">
              <p className="text-gray-600">불러올 사진이 없습니다.</p>
              <p className="text-sm text-gray-400">
                사진첩에서 먼저 사진을 업로드해주세요.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
              {galleryData.map((data) => (
                <div
                  key={data.ImgID}
                  className={`relative cursor-pointer aspect-square border-2 rounded-lg ${
                    modalSelectedImages?.find(
                      (item) => item.ImgID === data.ImgID
                    )
                      ? " border-orange-500"
                      : "border-white bg-white"
                  }`}
                  onClick={() => toggleImageSelection(data)}
                >
                  <img
                    src={data.ImgURL}
                    alt={`Gallery image ${data.ImgID}`}
                    className="object-cover w-full h-full rounded-lg"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImageModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={handleImgModal}
          >
            선택
          </button>
        </div>
      </MessageModal>

      {/* 성공 다이얼로그 */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">일정 공지 발송 완료</h3>
          <p className="mb-4 text-gray-600">
            일정 공지가 성공적으로 발송되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              router.navigate({ to: "/business/layout/notify" });
            }}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default SendSavedSchedule;
